import styled from 'styled-components'
import { MobileColors } from '../../styles/mobileTokens'
import { Z_INDEX } from '../../styles/z-index'

// Colors - references centralized design tokens matching Tailwind config and Figma
export const COLORS = {
  primary: MobileColors.primary, // #06512f (fb-green-900)
  background: MobileColors.backgroundPage, // #ffffff
  cardBackground: MobileColors.backgroundCard, // #F4F7F5 (sage-50)
  border: MobileColors.borderDefault, // #E2E2E2
  lightGray: MobileColors.backgroundOverlay, // #f9fafb (gray-50)
  mediumGray: MobileColors.textSecondary, // #6E6E6E
  text: MobileColors.textPrimary, // #191D23
  textLight: MobileColors.textSecondary, // #6E6E6E
  badge: MobileColors.backgroundBadge, // #E2E2E2
  badgeText: MobileColors.textSecondary, // #6E6E6E
}

// Page Container (for dedicated /search route)
export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: ${COLORS.background};
`

// Back Button
export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  border-bottom: 1px solid ${COLORS.border};
  background: ${MobileColors.accent}; /* Medium green (#2C6B4E) for visual separation from header */
  color: white;
  padding: 10px 20px;
  font-size: 16px;
  cursor: pointer;
  height: 40px;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`

// Search Container
export const SearchContainer = styled.div`
  padding: 12px 16px 6px 16px;
  background: ${COLORS.background};
`

// Dropdown
export const DropdownButton = styled.button`
  width: auto;
  padding: 6px 8px;
  background: transparent;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: ${COLORS.text};
  cursor: pointer;
  margin-bottom: 8px;

  &:hover {
    opacity: 0.7;
  }
`

// DropdownLabel, DropdownValue converted to Tailwind

export const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${COLORS.background};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  margin-top: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: ${Z_INDEX.MOBILE_DROPDOWN};
  overflow: hidden;
`

export const DropdownItem = styled.button<{ $isSelected?: boolean }>`
  width: 100%;
  padding: 14px 16px;
  background: ${(props) =>
    props.$isSelected ? COLORS.lightGray : 'transparent'};
  border: none;
  border-bottom: 1px solid ${COLORS.border};
  text-align: left;
  font-size: 14px;
  color: ${(props) => (props.$isSelected ? COLORS.primary : COLORS.text)};
  font-weight: ${(props) => (props.$isSelected ? '500' : '400')};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${COLORS.lightGray};
  }
`

// Search Input - SearchInputContainer, SearchInputWrapper, SearchInput, SearchIconWrapper, ClearButton converted to Tailwind

export const IconButton = styled.button`
  min-width: 48px;
  height: 48px;
  padding: 10px;
  background: ${COLORS.background};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${COLORS.text};

  &:hover {
    background: ${COLORS.lightGray};
  }

  svg {
    color: ${COLORS.text};
    fill: ${COLORS.text};
  }

  svg path {
    fill: ${COLORS.text};
  }
`

// SortButtonContent, SortLabel, SortDivider converted to Tailwind

// Results Area Wrapper (contains results and panels)
export const ResultsAreaWrapper = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
`

// Results
export const ResultsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

export const ResultsHeader = styled.div`
  padding: 4px 16px;
  font-size: 12px;
  color: ${COLORS.textLight};
  background: ${COLORS.background};
`

// Applied Filters
export const AppliedFiltersContainer = styled.div`
  padding: 0;
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  background: ${COLORS.background};
`

export const FilterTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: ${MobileColors.backgroundSelected};
  color: ${MobileColors.primary};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`

export const FilterTagLabel = styled.span`
  color: ${MobileColors.primary};
  font-size: 12px;
  font-weight: 600;
`

export const FilterTagValue = styled.span`
  color: ${MobileColors.primary};
  font-weight: 400;
`

export const FilterTagRemove = styled.button`
  background: none;
  border: none;
  color: ${MobileColors.primary};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-left: 2px;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: ${MobileColors.shadowBrand};
  }
`

export const ResultsList = styled.div`
  padding: 8px 16px 80px 16px; /* Extra bottom padding for fixed footer */
`

// Result Card components (ResultCard, CardHeader, CompanyInfo, CompanyLabel, CompanyLink, CompanyName, LocationName, CategoryBadge, AddressLine, AddressIcon, MetaLine) converted to Tailwind

// Empty State components (EmptyState, EmptyStateIcon, EmptyStateText, EmptyStateSubtext) converted to Tailwind

// Side Panels (Filter & Sort)
// Base panel styles shared between full-width and partial-width panels
const BasePanelStyles = `
  position: fixed;
  top: calc(
    var(--footer-height) + env(safe-area-inset-top) + 8px + 40px
  ); /* Below header (with safe area + padding) + back button */
  bottom: calc(
    60px + env(safe-area-inset-bottom)
  ); /* Above footer (with safe area) */
  background: ${COLORS.background};
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow-y: auto;
`

// Full-width side panel (used by Filter)
export const SidePanel = styled.div<{ $isOpen: boolean }>`
  ${BasePanelStyles}
  left: 0;
  right: 0;
  transform: ${(props) =>
    props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease-in-out;
  z-index: ${Z_INDEX.MOBILE_SEARCH_PANEL};
`

// 75% width side panel with backdrop (used by Sort)
export const SortPanel = styled.div<{ $isOpen: boolean }>`
  ${BasePanelStyles}
  right: 0;
  width: 75%;
  max-width: 400px;
  transform: ${(props) =>
    props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease-in-out;
  z-index: ${Z_INDEX.MOBILE_SEARCH_PANEL + 1};
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
`

// Darkened backdrop overlay (used by Sort)
export const SortOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${MobileColors.overlay};
  z-index: ${Z_INDEX.MOBILE_SEARCH_PANEL};
  opacity: ${(props) => (props.$isOpen ? '1' : '0')};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
`

export const PanelOverlay = styled.div<{ $isOpen: boolean }>`
  display: none; /* No overlay needed - panel covers entire area */
`

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${COLORS.border};
  background: ${COLORS.lightGray};
`

// PanelTitle converted to Tailwind

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.text};
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  font-size: 20px;

  &:hover {
    color: ${COLORS.primary};
  }
`

export const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`

export const PanelFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid ${COLORS.border};
  background: ${COLORS.lightGray};
`

export const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  min-height: 44px;

  ${(props) =>
    props.$variant === 'primary'
      ? `
    background: ${COLORS.primary};
    color: white;

    &:hover {
      background: ${MobileColors.primaryDark};
    }
  `
      : `
    background: transparent;
    color: ${COLORS.text};
    border: 1px solid ${COLORS.border};

    &:hover {
      background: ${COLORS.lightGray};
    }
  `}
`

// Filter/Sort Sections
export const FilterSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const FilterSectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
`

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const RadioOption = styled.label<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${(props) =>
    props.$isSelected ? COLORS.lightGray : 'transparent'};
  border: 1px solid
    ${(props) => (props.$isSelected ? COLORS.primary : COLORS.border)};
  border-radius: 8px;
  cursor: pointer;
  min-height: 44px;

  &:hover {
    background: ${COLORS.lightGray};
  }
`

export const RadioInput = styled.input`
  display: none;
`

export const RadioLabel = styled.span`
  font-size: 14px;
  color: ${COLORS.text};
`

export const RadioCheckmark = styled.span<{ $isSelected?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${(props) => (props.$isSelected ? COLORS.primary : COLORS.border)};
  background: ${(props) =>
    props.$isSelected ? COLORS.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
`

// Load More Button
export const LoadMoreButton = styled.button<{ disabled?: boolean }>`
  width: 50%;
  max-width: 200px;
  padding: 12px;
  margin: 12px auto;
  display: block;
  background: ${MobileColors.backgroundHover};
  color: ${MobileColors.accent};
  border: 1px solid ${MobileColors.accent};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.7 : 1)};

  &:hover:not(:disabled) {
    background: ${MobileColors.backgroundActive};
  }

  &:active:not(:disabled) {
    background: ${MobileColors.backgroundActive};
    opacity: 0.9;
  }
`

// Loading Container
export const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  padding-bottom: calc(
    60px + env(safe-area-inset-bottom) + 24px
  ); /* Footer height + safe area + extra padding */
`
